/**
 * DefaultPromptForm
 * Fallback prompt form used when no type-specific form is registered.
 * Renders interactionCatalog option fields + shared PromptDescriptionInput.
 */

import * as React from 'react';
import find from 'lodash/find';
import { cn, Button } from '@ai-content-hub/content-kit';
import { Send, RefreshCw, Loader2, Sparkles } from 'lucide-react';
import type { PromptFormProps } from './types';
import type { UploadedDocument } from './types';
import { buildDocumentSubmitFields } from './documentSubmit';
import { PromptDescriptionInput, JobDetailsBar, InteractionOptionsPanel } from './components';
import { usePromptTranslations } from './i18n';
import {
    resolveQuestionTypeDisplayName,
    useInteractionOptions,
} from './interactionOptions';

const MAX_LENGTH = 5000;

export const DefaultPromptForm: React.FC<PromptFormProps> = ({
    questionType,
    questionTypeOptions,
    allQuestionTypeOptions,
    onSubmit,
    onCancel,
    loading = false,
    disabled = false,
    isRegeneration = false,
    className,
    apiConfig,
    jobStatus,
    onRetry,
    onDismiss,
}) => {
    const [prompt, setPrompt] = React.useState('');
    const [documents, setDocuments] = React.useState<UploadedDocument[]>([]);
    const [sourceType, setSourceType] = React.useState('topic');
    const pt = usePromptTranslations();
    const { values: optionValues, updateField: updateOptionField } =
        useInteractionOptions(questionType);

    const optionList = allQuestionTypeOptions ?? questionTypeOptions ?? [];

    const aiContentType = React.useMemo(
        () =>
            find(
                optionList,
                (o) => o.slug === questionType || o.code === questionType,
            ) ?? null,
        [optionList, questionType],
    );

    const typeDisplayName = resolveQuestionTypeDisplayName(
        questionType,
        optionList,
    );
    const typeDescription = aiContentType?.description;

    const isDisabled = disabled || loading;
    const isFailed = jobStatus?.status === 'failed';
    const showRetry = isFailed && !!onRetry;
    const canSubmit = (prompt.trim().length > 0 || documents.length > 0) && !isDisabled;

    const handleSubmit = React.useCallback(() => {
        if (!canSubmit && !showRetry) return;
        if (showRetry && onRetry) {
            onRetry();
            return;
        }
        onSubmit({
            prompt: prompt.trim(),
            ...buildDocumentSubmitFields(documents),
            option: Object.keys(optionValues).length > 0 ? { ...optionValues } : undefined,
        });
    }, [canSubmit, showRetry, onRetry, prompt, documents, onSubmit, optionValues]);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && (canSubmit || showRetry)) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [canSubmit, showRetry, handleSubmit],
    );

    return (
        <div className={cn('default-prompt-form w-full min-h-0', className)}>
            <div className="default-prompt-form-panel overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow duration-200 hover:shadow-md">
                {(typeDisplayName || typeDescription) && (
                    <div className="default-prompt-form-type-header border-b border-border/70 bg-muted/25 px-5 py-4 sm:px-6 sm:py-5">
                        <div className="flex gap-3 sm:gap-4">
                            <div
                                className="default-prompt-form-type-icon mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15"
                                aria-hidden
                            >
                                <Sparkles className="h-4 w-4" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1 space-y-1.5">
                                {typeDisplayName ? (
                                    <h2 className="default-prompt-form-type-name text-base font-semibold leading-snug tracking-tight text-foreground sm:text-lg">
                                        {typeDisplayName}
                                    </h2>
                                ) : null}
                                {typeDescription ? (
                                    <p className="default-prompt-form-type-description text-sm leading-relaxed text-muted-foreground sm:max-w-3xl">
                                        {typeDescription}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}

                <InteractionOptionsPanel
                    questionType={questionType}
                    values={optionValues}
                    onFieldChange={updateOptionField}
                    disabled={isDisabled}
                />

                <div className="default-prompt-form-prompt-body">
                    <PromptDescriptionInput
                        value={prompt}
                        onChange={setPrompt}
                        onKeyDown={handleKeyDown}
                        activePresetKey={sourceType}
                        onActivePresetKeyChange={setSourceType}
                        documents={documents}
                        onDocumentsChange={setDocuments}
                        apiConfig={apiConfig}
                        disabled={isDisabled}
                        maxLength={MAX_LENGTH}
                        label={pt.prompt}
                        labelOptional={false}
                        placeholder={pt.promptPlaceholder}
                        className="default-prompt-form-card rounded-none border-0 bg-transparent shadow-none ring-0"
                    />
                </div>

                <div className="default-prompt-form-footer flex flex-col gap-3 border-t border-border/80 bg-muted/15 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-4">
                    <div className="default-prompt-form-footer-status min-w-0 flex flex-1 items-center justify-start">
                        <JobDetailsBar
                            jobStatus={jobStatus ?? null}
                            onDismiss={onDismiss}
                            className="default-prompt-form-job-details"
                        />
                    </div>
                    <div className="default-prompt-form-footer-actions flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
                        {onCancel && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onCancel}
                                disabled={isDisabled}
                                className="min-h-11 min-w-[88px] px-5 transition-colors duration-200"
                            >
                                {pt.cancel}
                            </Button>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleSubmit}
                            disabled={!canSubmit && !showRetry}
                            className="default-prompt-form-submit min-h-11 gap-2 px-6 font-medium shadow-sm transition-colors duration-200 hover:opacity-[0.97] disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : showRetry ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : isRegeneration ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                            {loading
                                ? pt.generating
                                : showRetry
                                  ? pt.retry
                                  : isRegeneration
                                    ? pt.regenerate
                                    : pt.generate}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DefaultPromptForm;
